import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Sparkles, Languages, Loader2 } from "lucide-react";
import { renderLine } from "../components/AIContent";
import { ListenButton } from "../components/ListenButton";
import { useLanguage, T } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArticleDetailPage() {
  const { articleId } = useParams();
  const { lang, langName } = useLanguage();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [translated, setTranslated] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setArticle(null);
    setTranslated(null);
    setRelated([]);
    axios.get(`${API}/articles/${articleId}`).then((r) => setArticle(r.data)).catch(() => setNotFound(true));
    axios.get(`${API}/articles/${articleId}/related`).then((r) => setRelated(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, [articleId]);

  useEffect(() => { setTranslated(null); }, [lang]);

  const translateArticle = async () => {
    if (!article) return;
    setTranslating(true);
    try {
      const paras = article.content.split("\n").filter((p) => p.trim()).slice(0, 60).map((p) => p.slice(0, 500));
      const res = await axios.post(`${API}/translate`, { texts: paras, language: langName });
      setTranslated(res.data.translations.join("\n"));
    } catch {}
    setTranslating(false);
  };

  if (notFound) return <div className="py-24 text-center text-slate-500">Article not found. <Link to="/articles" className="text-[#0055FF] underline">All articles</Link></div>;
  if (!article) return <div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-4 border-[#0055FF] border-t-transparent" /></div>;

  const body = translated || article.content;

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6" id="page-content">
      <Link to="/articles" data-testid="back-to-articles" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0055FF] hover:underline"><ArrowLeft className="h-4 w-4" /> <T>All articles</T></Link>
      <div className="mt-6 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-bold text-blue-700"><Sparkles className="h-3 w-3" /><T>AI-generated</T></span>
        <span className="rounded-full bg-purple-100 px-2.5 py-1 text-xs font-bold text-purple-700"><T>{article.section}</T></span>
        {article.tags.map((t) => <span key={t} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">#{t}</span>)}
      </div>
      <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl"><T>{article.title}</T></h1>
      <p className="mt-2 text-sm text-slate-400"><T>Published</T>: {new Date(article.created_at).toLocaleDateString()}</p>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <ListenButton targetId="article-body" />
        {lang !== "en" && !translated && (
          <button data-testid="translate-article-button" onClick={translateArticle} disabled={translating} className="inline-flex items-center gap-2 rounded-full border border-[#0055FF] px-4 py-2 text-sm font-bold text-[#0055FF] hover:bg-blue-50 disabled:opacity-50">
            {translating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Languages className="h-4 w-4" />}
            <T>Translate this article</T> ({langName})
          </button>
        )}
        {translated && <span className="rounded-full bg-emerald-100 px-3 py-1.5 text-xs font-bold text-emerald-700" data-testid="machine-translated-label"><T>Machine translated</T> · {langName}</span>}
      </div>
      <article id="article-body" className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 sm:p-10" data-testid="article-body">
        {body.split("\n").map(renderLine)}
      </article>
      {related.length > 0 && (
        <section className="mt-10" data-testid="related-articles">
          <h2 className="text-lg font-extrabold text-slate-900"><T>You might also like</T></h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            {related.map((r) => (
              <Link key={r.article_id} to={`/articles/${r.article_id}`} data-testid={`related-article-${r.article_id}`}
                className="group rounded-2xl border border-slate-200 bg-gradient-to-br from-blue-50 to-white p-5 transition-shadow hover:shadow-md">
                <span className="rounded-full bg-purple-100 px-2.5 py-1 text-xs font-bold text-purple-700"><T>{r.section}</T></span>
                <p className="mt-3 text-sm font-bold leading-snug text-slate-900 group-hover:text-[#0055FF]"><T>{r.title}</T></p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(r.tags || []).slice(0, 3).map((t) => <span key={t} className="text-xs font-semibold text-slate-400">#{t}</span>)}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
